import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { mainSiteUrl, categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link
          to="/"
          className="text-lg font-semibold text-foreground-900 tracking-tight whitespace-nowrap hover:text-primary-500 transition-colors cursor-pointer"
        >
          Sugargoo Spreadsheet
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
          >
            Home
          </Link>
          <Link
            to="/sugargoo-spreadsheet-ultimate-guide"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
          >
            Ultimate Guide
          </Link>
          <Link
            to="/sugargoo-review"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
          >
            Review
          </Link>
          <Link
            to="/sugargoo-shipping-cost"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
          >
            Shipping
          </Link>
          <Link
            to="/sugargoo-buying-guide"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
          >
            Shopping Guide
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Sugargoo Spreadsheet
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-xs"></i>
            </span>
          </a>
        </div>

        {/* Mobile hamburger button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-home-line text-base"></i>
            </span>
            Home
          </Link>
          <Link
            to="/sugargoo-spreadsheet-ultimate-guide"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-book-open-line text-base"></i>
            </span>
            Ultimate Guide
          </Link>
          <Link
            to="/sugargoo-review"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-star-line text-base"></i>
            </span>
            Sugargoo Review
          </Link>
          <Link
            to="/sugargoo-shipping-cost"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-ship-line text-base"></i>
            </span>
            Shipping Cost Guide
          </Link>
          <Link
            to="/sugargoo-coupon"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-coupon-line text-base"></i>
            </span>
            Coupon &amp; Discount
          </Link>
          <Link
            to="/sugargoo-warehouse"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-archive-line text-base"></i>
            </span>
            Warehouse Guide
          </Link>
          <Link
            to="/sugargoo-buying-guide"
            onClick={closeMobile}
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-shopping-cart-line text-base"></i>
            </span>
            Shopping Guide
          </Link>

          {/* Mobile category toggle */}
          <button
            onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
            className="w-full flex items-center justify-between text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer mt-1 border-t border-background-200 pt-3"
          >
            <span className="flex items-center">
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-grid-line text-base"></i>
              </span>
              Product Categories
            </span>
            <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileCategoriesOpen ? "rotate-180" : ""}`}>
              <i className="ri-arrow-down-s-line text-sm"></i>
            </span>
          </button>

          {mobileCategoriesOpen && (
            <div className="grid grid-cols-2 gap-1 px-3 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center gap-2 text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors py-2 px-2 rounded-lg hover:bg-background-100 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            onClick={closeMobile}
            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Sugargoo Spreadsheet
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-xs"></i>
            </span>
          </a>
        </div>
      )}
    </nav>
  );
}